/**
 * brand.ts — single source of truth for brand/country parameterisation.
 *
 * The same codebase serves multiple brands, selected AT BUILD TIME via
 * NEXT_PUBLIC_BRAND (baked into the bundle by Next.js):
 *
 *   - 'camping-australia' (default): the existing Camping Australia explorer,
 *     served as a Shopify app-proxy under /apps/campsite-explorer.
 *     Behaviour for this brand must remain EXACTLY as before this module
 *     existed — it is the regression-must-not-happen brand.
 *
 *   - 'escapeto': EscapeTo (escapeto.travel), served at the domain root.
 *     Country is resolved per-request from the URL path segment
 *     (/us, /au, /ca, /nz) with a default of US — see getCountry().
 *
 * Everything brand- or country-specific in the app should flow through this
 * module: link prefixes (withBase), country threading for API calls
 * (getCountry), display strings (BRAND.name etc.) and feature flags.
 */

export type BrandKey = 'camping-australia' | 'escapeto';
export type Country = 'AU' | 'US' | 'CA' | 'NZ' | 'AR' | 'CL';

export interface BrandColors {
  /** Primary action / accent colour. */
  primary: string;
  /** Dark ground (headers, footers, dark surfaces). */
  dark: string;
  /** Optional highlight / gold accent. */
  gold?: string;
}

export interface BrandConfig {
  key: BrandKey;
  /** Human brand name — titles, footer, JSON-LD publisher. */
  name: string;
  /** Default country for API country threading (see getCountry()). */
  country: Country;
  /** Canonical public origin, no trailing slash. */
  siteUrl: string;
  /**
   * Path prefix every internal link must carry. Camping Australia is mounted
   * behind the Shopify app proxy at /apps/campsite-explorer; EscapeTo is
   * served at the domain root (empty prefix). Use withBase() — do not
   * concatenate this manually.
   */
  basePath: string;
  /** OpenGraph locale, e.g. en_AU. */
  locale: string;
  /** Country display name — used for whole-country searches + copy. */
  countryName: string;
  /** Twitter/X handle for metadata (undefined = omit). */
  twitterHandle?: string;
  /**
   * Real, verified profile URLs for schema.org sameAs on the Organization node.
   * sameAs is how an AI or search engine resolves this site to a known entity;
   * geo.md counts it as one of the five schema checks. Only URLs confirmed to
   * return 200 belong here - a dead profile is worse than none. The store's
   * footer also links twitter.com/campingaus2, which 404s, so it is excluded.
   */
  profiles?: string[];
  /** GA4 measurement id for client-side gtag (undefined = no analytics). */
  gaMeasurementId?: string;
  colors: BrandColors;
  /** Initial map view for the explorer. */
  mapCenter: { lat: number; lng: number };
  mapZoom: number;
  // ── Feature flags ──────────────────────────────────────────────
  /** Shopify commerce surface: gear CTAs, cart, mega menu, shop links. */
  showShopifyGear: boolean;
  /** Bookeasy live availability/booking widgets (AU booking pilot). */
  showBookeasy: boolean;
  /** AFDRS-style fire danger widget (Australian ratings system). */
  showFireDanger: boolean;
  /** Nearby fuel stations rail (Google Places — country-agnostic). */
  showFuelStations: boolean;
  /** Region browse UI — only AU has Region nodes in the graph. */
  showRegions: boolean;
  /** Campervan-hire cross-sell (AU operators only). */
  showVanHire: boolean;
  /** Native app smart banner + store badges (Camping Australia apps). */
  showAppBanner: boolean;
  /**
   * Apple Sign-In web client id (the Apple "Service ID") passed to
   * AppleID.auth.init on the ReviewerLoginPrompt. escapeto.travel and
   * campingaustralia.com.au are different origins and so need DIFFERENT
   * Service IDs — Apple ties a Service ID to its registered redirect/return
   * URLs. Using one brand's id on the other's origin used to silently mint
   * an Apple identity token whose `aud` claim was the WRONG brand's, which
   * the server's resolve_brand() then trusted as authoritative and branded
   * the sign-in camping-australia even on escapeto.travel (see the server's
   * user_scope.ESCAPETO_WEB_APPLE_AUD docstring).
   *
   * '' means Apple Sign-In is not yet configured for this brand's web
   * surface — callers must check for that and not fall back to another
   * brand's id (see ReviewerLoginPrompt.handleAppleSignIn).
   */
  appleWebClientId: string;
}

const BRANDS: Record<BrandKey, BrandConfig> = {
  'camping-australia': {
    key: 'camping-australia',
    name: 'Camping Australia',
    country: 'AU',
    siteUrl: 'https://campingaustralia.com.au',
    basePath: '/apps/campsite-explorer',
    locale: 'en_AU',
    countryName: 'Australia',
    twitterHandle: '@campingaustralia',
    profiles: [
      'https://www.facebook.com/campingaust/',
      'https://www.instagram.com/campingaust/',
      'https://www.youtube.com/@campingaus',
      'https://www.pinterest.com/0jnlv50hyrz11zj2jfryvtorq9v2ld',
    ],
    gaMeasurementId: 'G-P00P65FNEL',
    colors: {
      primary: '#1a68d1', // Field Blue — DESIGN.md canonical accent (was #3b82f6)
      dark: '#1e293b',
    },
    // Shifted south to include Tasmania (existing behaviour)
    mapCenter: { lat: -27.5, lng: 133.7751 },
    mapZoom: 3.9,
    showShopifyGear: true,
    showBookeasy: true,
    showFireDanger: true,
    showFuelStations: true,
    showRegions: true,
    showVanHire: true,
    showAppBanner: true,
    // Unchanged from before this field existed — same env var, same default.
    appleWebClientId: process.env.NEXT_PUBLIC_APPLE_WEB_CLIENT_ID || 'com.campingaustralia.web',
  },
  escapeto: {
    key: 'escapeto',
    name: 'EscapeTo',
    country: 'US',
    siteUrl: 'https://escapeto.travel',
    basePath: '',
    locale: 'en_US',
    countryName: 'United States',
    twitterHandle: undefined,
    gaMeasurementId: 'G-YYGKZ74Q07',
    colors: {
      primary: '#E4632B', // ember
      dark: '#123129', // pine
      gold: '#EDB458',
    },
    // Continental US overview
    mapCenter: { lat: 39.5, lng: -98.35 },
    mapZoom: 4.2,
    showShopifyGear: false,
    showBookeasy: false,
    showFireDanger: false,
    // Fuel lookup is Google Places by lat/lng — not AU-specific.
    showFuelStations: true,
    // No Region nodes outside AU — hide region browse UI (must degrade, not crash).
    showRegions: false,
    showVanHire: false,
    showAppBanner: false,
    // '' (not campingaustralia.com.au's id) until the owner registers
    // escapeto.travel's own Apple Service ID and sets this env var — see
    // appleWebClientId's docstring above for why the two brands cannot share
    // one id.
    appleWebClientId: process.env.NEXT_PUBLIC_APPLE_ESCAPETO_WEB_CLIENT_ID || '',
  },
};

function resolveBrand(): BrandConfig {
  const key = (process.env.NEXT_PUBLIC_BRAND || 'camping-australia') as BrandKey;
  return BRANDS[key] || BRANDS['camping-australia'];
}

export const BRAND: BrandConfig = resolveBrand();

/**
 * Paths that point at static ASSETS rather than app routes. These must never
 * carry the escapeto country segment (there is no /us/favicon.ico) — they are
 * served from the domain root by the public/ folder.
 */
function isAssetPath(p: string): boolean {
  const pathOnly = p.split(/[?#]/)[0];
  if (/^\/(images|_next|shopify-theme)\//.test(pathOnly)) return true;
  // File-style targets (favicon.ico, manifest.json, webmcp.js, …). Route slugs
  // are [a-z0-9-] so a dotted final segment is always a real file.
  return /\.[A-Za-z0-9]{2,5}$/.test(pathOnly);
}

/**
 * Prefix an app-internal path with the brand's base path.
 *
 *   withBase('/camp-sites/foo') → '/apps/campsite-explorer/camp-sites/foo' (CA)
 *                               → '/us/camp-sites/foo'                     (EscapeTo, US active)
 *   withBase('')                → '/apps/campsite-explorer' (CA) / '/us' (EscapeTo)
 *   withBase('?location=x')     → '/apps/campsite-explorer?location=x' (CA)
 *                               → '/us?location=x'                     (EscapeTo)
 *
 * EscapeTo internal links carry the ACTIVE COUNTRY segment (/us /au /ca /nz);
 * asset paths (images, favicons, files) are exempt. camping-australia output
 * is byte-identical to the pre-brand-module hard-coded links.
 */
export function withBase(path?: string): string {
  const base = BRAND.basePath;
  let p = path ?? '';
  if (BRAND.key === 'escapeto') {
    const prefix = `/${getCountry().toLowerCase()}`;
    // Query- or hash-only targets attach to the active country home.
    if (p.startsWith('?') || p.startsWith('#')) return prefix + p;
    if (p && !p.startsWith('/')) p = '/' + p;
    if (p && isAssetPath(p)) return p;
    return p ? prefix + p : prefix;
  }
  // camping-australia — unchanged legacy behaviour.
  if (p.startsWith('?') || p.startsWith('#')) return (base || '/') + p;
  if (p && !p.startsWith('/')) p = '/' + p;
  if (!base) return p || '/';
  if (!p) return base;
  return base + p;
}

const COUNTRY_SEGMENTS: Record<string, Country> = {
  us: 'US',
  au: 'AU',
  ca: 'CA',
  nz: 'NZ',
  ar: 'AR',
  cl: 'CL',
};

/** Lowercase URL segments for every supported country, in display order. */
export const COUNTRY_SEGMENT_LIST = ['us', 'au', 'ca', 'nz', 'ar', 'cl'] as const;

/** Map a URL path segment ('us' | 'au' | …) to a Country, or null. */
export function countryFromSegment(seg?: string | null): Country | null {
  if (!seg) return null;
  return COUNTRY_SEGMENTS[seg.toLowerCase()] || null;
}

/** Cookie that remembers the visitor's preferred country (escapeto only). */
export const COUNTRY_COOKIE = 'et_country';

/**
 * Robots directive for the AU-only SEO lander pages (camping-near-sydney,
 * caravan-finance, …): on escapeto they'd be irrelevant AU content competing
 * with the country trees, so they are noindexed there. undefined (no override)
 * on camping-australia — spread as `...(AU_LANDER_ROBOTS ?? {})`.
 */
export const AU_LANDER_ROBOTS: { robots: { index: boolean; follow: boolean } } | undefined =
  BRAND.key === 'escapeto' ? { robots: { index: false, follow: false } } : undefined;

// ── Request/render-scoped country override (escapeto only) ─────────────
//
// getCountry() is a plain function called from both server and client module
// graphs, so the country override has two mechanisms:
//
// 1. serverCountryResolver — registered by app/lib/countryScope.server.ts
//    (AsyncLocalStorage). Used by the app/[country]/* server wrappers so
//    concurrent SSR requests can't bleed countries into each other.
// 2. countryOverride — a plain module variable set synchronously during
//    render by <CountryScope> (app/components/CountryScope.tsx). This covers
//    the SSR pass of CLIENT components (where AsyncLocalStorage and window
//    are both unavailable). In the browser the URL path always wins anyway.
type CountryResolver = () => Country | null;
let serverCountryResolver: CountryResolver | null = null;
export function registerCountryResolver(resolver: CountryResolver): void {
  serverCountryResolver = resolver;
}

let countryOverride: Country | null = null;
export function setCountryOverride(country: Country | null): void {
  countryOverride = country;
}

/**
 * Resolve the active country for API country threading.
 *
 * - camping-australia: always 'AU' (no selector — unchanged behaviour).
 * - escapeto: the brand serves all four countries from one domain via a
 *   path prefix (escapeto.travel/us|/au|/ca|/nz). Resolution order:
 *     1. server request scope (AsyncLocalStorage, [country] wrappers)
 *     2. browser URL path segment
 *     3. render-scoped override (<CountryScope> during client-component SSR)
 *     4. remembered preference cookie (browser, unprefixed paths)
 *     5. brand default ('US')
 *
 * Always call getCountry() at request time rather than caching its result in
 * module scope.
 */
export function getCountry(): Country {
  if (BRAND.key === 'camping-australia') return BRAND.country;
  const resolved = serverCountryResolver?.();
  if (resolved) return resolved;
  if (typeof window !== 'undefined') {
    const seg = window.location.pathname.split('/').filter(Boolean)[0]?.toLowerCase();
    if (seg && COUNTRY_SEGMENTS[seg]) return COUNTRY_SEGMENTS[seg];
  }
  if (countryOverride) return countryOverride;
  if (typeof document !== 'undefined') {
    const m = document.cookie.match(/(?:^|;\s*)et_country=(us|au|ca|nz|ar|cl)\b/);
    if (m) return COUNTRY_SEGMENTS[m[1]];
  }
  return BRAND.country;
}

const COUNTRY_NAMES: Record<Country, string> = {
  AU: 'Australia',
  US: 'United States',
  CA: 'Canada',
  NZ: 'New Zealand',
  AR: 'Argentina',
  CL: 'Chile',
};

/** Display name for the active country (whole-country searches, copy). */
export function getCountryName(): string {
  return COUNTRY_NAMES[getCountry()];
}

/** ISO 3166-1 alpha-2 in lowercase — e.g. Nominatim countrycodes param. */
export function getCountryCode(): string {
  return getCountry().toLowerCase();
}

/** Whole-country bounding box for country-wide bounds searches. */
export function getCountryBounds(): { north: number; south: number; east: number; west: number } {
  const BOUNDS: Record<Country, { north: number; south: number; east: number; west: number }> = {
    AU: { north: -10, south: -45, east: 155, west: 112 }, // existing AU values
    US: { north: 49.5, south: 24.4, east: -66.9, west: -125 }, // contiguous US
    CA: { north: 70, south: 41.7, east: -52.6, west: -141 },
    NZ: { north: -34.3, south: -47.4, east: 178.6, west: 166.3 },
    AR: { north: -21.8, south: -55.1, east: -53.6, west: -73.6 },
    CL: { north: -17.5, south: -56.0, east: -66.4, west: -75.7 },
  };
  return BOUNDS[getCountry()];
}

/** Initial map view for the active country. */
export function getMapDefaults(): { center: { lat: number; lng: number }; zoom: number } {
  const country = getCountry();
  if (country === BRAND.country) {
    return { center: BRAND.mapCenter, zoom: BRAND.mapZoom };
  }
  const CENTERS: Record<Country, { center: { lat: number; lng: number }; zoom: number }> = {
    AU: { center: { lat: -27.5, lng: 133.7751 }, zoom: 3.9 },
    US: { center: { lat: 39.5, lng: -98.35 }, zoom: 4.2 },
    CA: { center: { lat: 56.1, lng: -106.3 }, zoom: 3.5 },
    NZ: { center: { lat: -41.3, lng: 172.5 }, zoom: 5 },
    AR: { center: { lat: -38.4, lng: -63.6 }, zoom: 3.6 },
    CL: { center: { lat: -35.7, lng: -71.5 }, zoom: 3.3 },
  };
  return CENTERS[country];
}
